/**
 * Patron Points — HOME. Read only.
 *
 * Number tiles, two charts and "What we noticed", all computed server-side
 * from the ledger and the visit rows. When there is nothing to say, it says
 * so: no invented numbers anywhere on this screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { Coins, Gift, Search, Sparkles, Users, Eye } from 'lucide-react';
import api from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';

const num = (n) => Number(n || 0).toLocaleString();

const Tile = ({ icon: Icon, label, value, testid }) => (
    <Card data-testid={testid}>
        <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-slate-500">
                <Icon size={12} className="inline mr-1 -mt-0.5" />{label}
            </p>
            <p className="text-2xl font-bold tabular-nums">{value}</p>
        </CardContent>
    </Card>
);

export const PatronHome = ({ orgId, range, onOpenPlayer }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [q, setQ] = useState('');
    const [hits, setHits] = useState(null);

    useEffect(() => {
        if (!orgId) return;
        (async () => {
            const params = { organization_id: orgId };
            if (range?.start) params.start = new Date(range.start).toISOString();
            if (range?.end) params.end = new Date(range.end).toISOString();
            const res = await api.get('/patron/insights/home', { params });
            setData(res.data);
        })();
    }, [orgId, range]);

    const lookup = useCallback(async () => {
        const res = await api.get('/patron/insights/players', {
            params: { organization_id: orgId, q, page_size: 5 },
        });
        setHits(res.data.rows);
    }, [orgId, q]);

    if (!data) return <p className="text-sm text-slate-500" data-testid="patron-home-loading">…</p>;

    const tiles = data.tiles || {};
    return (
        <div className="space-y-6" data-testid="patron-home">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Tile icon={Users} label={t('patronCommand.tiles.players')}
                      value={num(tiles.players)} testid="patron-tile-players" />
                <Tile icon={Coins} label={t('patronCommand.tiles.pointsAwarded')}
                      value={num(tiles.points_awarded)} testid="patron-tile-awarded" />
                <Tile icon={Coins} label={t('patronCommand.tiles.pointsSpent')}
                      value={num(tiles.points_spent)} testid="patron-tile-spent" />
                <Tile icon={Gift} label={t('patronCommand.tiles.vouchers')}
                      value={num(tiles.vouchers)} testid="patron-tile-vouchers" />
                <Tile icon={Eye} label={t('patronCommand.tiles.visitsTonight')}
                      value={num(tiles.visits_tonight)} testid="patron-tile-visits-tonight" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card data-testid="patron-chart-points">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t('patronCommand.charts.points')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {(data.points_over_time || []).length === 0 ? (
                            <p className="text-sm text-slate-500 py-10 text-center"
                               data-testid="patron-chart-points-empty">
                                {t('patronCommand.charts.noPoints')}
                            </p>
                        ) : (
                            <ResponsiveContainer width="100%" height={200} minHeight={200}>
                                <LineChart data={data.points_over_time}
                                           margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                                    <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }}
                                           tickFormatter={(d) => String(d).slice(5)} />
                                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="points" stroke="#10b981"
                                          strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="patron-chart-visits">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t('patronCommand.charts.visits')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {(data.visits_per_night || []).length === 0 ? (
                            <p className="text-sm text-slate-500 py-10 text-center"
                               data-testid="patron-chart-visits-empty">
                                {t('patronCommand.charts.noVisits')}
                            </p>
                        ) : (
                            <ResponsiveContainer width="100%" height={200} minHeight={200}>
                                <BarChart data={data.visits_per_night}
                                          margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                                    <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
                                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="visits" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card data-testid="patron-noticed">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles size={15} className="text-emerald-500" />
                        {t('patronCommand.noticed.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {(data.noticed || []).length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="patron-noticed-empty">
                            {t('patronCommand.noticed.empty')}
                        </p>
                    ) : data.noticed.map((n) => (
                        <div key={n.id} data-testid={`patron-noticed-${n.id}`}
                             className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm">
                            {t(`patronCommand.noticed.${n.id}`, {
                                count: n.count, points: num(n.points),
                                hours: n.hours, days: n.days,
                            })}
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card data-testid="patron-lookup">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('patronCommand.lookup.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex gap-2">
                        <Input value={q} onChange={(e) => setQ(e.target.value)}
                               onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                               placeholder={t('patronCommand.lookup.placeholder')}
                               data-testid="patron-lookup-input" />
                        <Button variant="outline" onClick={lookup} data-testid="patron-lookup-go">
                            <Search size={15} />
                        </Button>
                    </div>
                    {hits && hits.length === 0 && (
                        <p className="text-sm text-slate-500" data-testid="patron-lookup-none">
                            {t('patronCommand.lookup.none')}
                        </p>
                    )}
                    {(hits || []).map((row) => (
                        <button key={row.id} onClick={() => onOpenPlayer(row.id)}
                                data-testid={`patron-lookup-hit-${row.id}`}
                                className="w-full flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                            <span>{row.screen_name || row.email}</span>
                            <span className="tabular-nums text-slate-500">{num(row.points)}</span>
                        </button>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
};
